import * as React from 'react'
import { useForkRef } from '@applift/factor'
import { useGridRootProps } from '../../hooks/utils/useGridRootProps'
import { ReactTableCellContext } from '../../models/gridReactTable'
import { GridRowSelectionCheckboxParams } from '../../models/params/gridRowSelectionCheckboxParams'
import { isSpaceKey } from '../../utils/keyboardUtils'

interface TouchRippleActions {
  stop: (event: any, callback?: () => void) => void
}

export const GridCellCheckboxRenderer = React.forwardRef<
  HTMLInputElement,
  ReactTableCellContext
>(function GridCellCheckboxRef(props, ref) {
  const { row, tabIndex, hasFocus, column, apiRef } = props

  const rootProps = useGridRootProps()
  const checkboxElement = React.useRef<HTMLInputElement | null>(null)
  const rippleRef = React.useRef<TouchRippleActions>()
  const handleRef = useForkRef(checkboxElement, ref)
  const element = apiRef.getCellElement(row.id, column.id)

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const params: GridRowSelectionCheckboxParams = {
      value: event.target.checked,
      id: row.id,
    }
    apiRef.publishEvent('rowSelectionCheckboxChange', params, event)
  }

  React.useLayoutEffect(() => {
    if (tabIndex === 0 && element) {
      element!.tabIndex = -1
    }
  }, [element, tabIndex])

  React.useEffect(() => {
    if (hasFocus) {
      const input = checkboxElement.current?.querySelector('input')
      input?.focus({ preventScroll: true })
    } else if (rippleRef.current) {
      rippleRef.current.stop({})
    }
  }, [hasFocus])

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (isSpaceKey(event.key)) {
        // We call event.stopPropagation to avoid selecting the row and also scrolling to bottom
        event.stopPropagation()
      }
    },
    []
  )

  const isSelectable = row.getCanSelect()
  const isChecked = row.getIsSelected()

  const label = isChecked
    ? 'checkboxSelectionUnselectRow'
    : 'checkboxSelectionSelectRow'

  return (
    <rootProps.components.BaseCheckbox
      ref={handleRef}
      checked={isChecked}
      onChange={handleChange}
      inputProps={{ 'aria-label': label }}
      onKeyDown={handleKeyDown}
      disabled={!isSelectable}
      tabIndex={tabIndex}
      {...rootProps.componentsProps?.baseCheckbox}
    />
  )
})
